import { discountLabel, formatCents } from './introOffer';

/**
 * The web's membership offers, priced from GET /subscription-plans, shared by
 * every paywall on the web: the funnel's (components/funnel/FunnelCheckout),
 * the website wall (components/TrialWall) and the Settings modal
 * (components/SubscriptionModal). There are three paywalls (2026-10-25):
 *
 *   normal   — Pro from the funnel's fixed coupons on the regular Stripe
 *              price: the first month in the experiment arm PostHog dealt the
 *              visit (hooks/useOfferVariant: control $7.99 / lower $4.99 /
 *              pwyw "pay what you want"), the first year $119.99 in every arm;
 *              Family at the 45% coupon. `funnel_intro_offer` and
 *              `funnel_offer_variants` on the plans response.
 *   winback  — 33% off the first 3 monthly invoices (the win-back email).
 *   trial    — 3 days free, card required: a Moments buyer who never had a
 *              trial (`subscriptionStatus.trialConfig`).
 *
 * Nothing here holds a price: every number comes from the backend, which reads
 * Stripe. A page names the arm it showed and the backend prices the session
 * from its own table.
 */

export const PRICING_UNAVAILABLE = "Sorry, pricing isn't available right now. Please try again later.";
export const SELLABLE_TIERS = ['pro', 'family'];

// The two plans on sale, as every paywall describes them (the website wall,
// the Settings modal, the funnel's plan grid): one copy, so no two screens
// can list different features. No amounts live here. Every account gets up
// to 4 profiles and 3-minute stories on any plan; Family's real extras are
// the stars and watermark-free downloads.
export const PLAN_COPY = {
  pro: {
    tier: 'pro',
    name: 'Pro',
    blurb: 'Best for most families',
    starsPerWeek: 250,
    badge: 'MOST POPULAR',
    features: ['Up to 3 min stories', '9:16 vertical + 16:9', 'All languages'],
  },
  family: {
    tier: 'family',
    name: 'Family',
    blurb: 'For multiple kids',
    starsPerWeek: 650,
    badge: null,
    features: ['Up to 3 min stories', 'Watermark-free downloads', 'All languages'],
  },
};
export const SELLABLE_PLANS = [PLAN_COPY.pro, PLAN_COPY.family];

// { [tier]: { monthly, annual } } in cents, from `plans[].price_cents` — what
// Stripe says the prices are. Nothing on a paywall is priced any other way.
export const pricesFrom = (plansData) => {
  const out = {};
  for (const plan of plansData?.plans || []) {
    const cents = plan?.price_cents;
    if (plan?.id && cents) out[plan.id] = { monthly: cents.monthly, annual: cents.annual };
  }
  return out;
};

// A plan with either amount missing is not priceable and must not be rendered
export const priceable = (prices, tier) => {
  const p = prices?.[tier];
  return !!p && Number.isFinite(p.monthly) && p.monthly > 0 && Number.isFinite(p.annual) && p.annual > 0;
};

// ── the paywall offer experiment ───────────────────────────────────────────
// Three arms on the Pro card, each priced by the backend and served in
// `funnel_offer_variants` on /subscription-plans, keyed by arm. An arm the
// backend reports unconfigured (a coupon id not set yet), or a plans response
// from before the experiment, is control: the page shows control's price and
// names control on checkout, so what is reported is what was charged.

export const servedOfferVariant = (plansData, variant) => {
  const arm = plansData?.funnel_offer_variants?.[variant];
  return arm && arm.configured !== false ? variant : 'control';
};

/** The offer block (same shape as `funnel_intro_offer`) for the arm that is
 * actually served to this visitor. */
export const funnelOfferFor = (plansData, variant = 'control') => {
  const control = plansData?.funnel_intro_offer || null;
  const served = servedOfferVariant(plansData, variant);
  // Control is `funnel_intro_offer` itself; and when Stripe could not be read
  // (control unavailable) every arm is, since they read the same prices
  if (served === 'control' || control?.available === false) return control;
  return plansData.funnel_offer_variants[served];
};

// Pay what you want: the price the parent named replaces Pro's suggested
// first month, said with its own discount label ("65%+" for $4.99), computed
// the way the backend computes it so the chip can never disagree.
const withChosenCents = (funnelOffer, chosenCents) => {
  const pro = funnelOffer?.plans?.pro;
  const regular = pro?.regular_cents?.monthly;
  if (!Number.isInteger(chosenCents) || !pro || !Number.isFinite(regular)) return funnelOffer;
  return {
    ...funnelOffer,
    plans: {
      ...funnelOffer.plans,
      pro: {
        ...pro,
        first_period_cents: { ...pro.first_period_cents, monthly: chosenCents },
        discount_label: { ...pro.discount_label, monthly: discountLabel(regular, chosenCents) || null },
      },
    },
  };
};

/** The pwyw chooser's terms for the month ({ stops, min_cents, max_cents,
 * suggested_cents }), or null when this visitor doesn't get a chooser (another
 * arm, annual billing, or no such block from the server). */
export const pwywChoiceFor = (plansData, variant, billing) => (
  servedOfferVariant(plansData, variant) === 'pwyw' && billing === 'monthly'
    ? funnelOfferFor(plansData, variant)?.plans?.pro?.choose?.monthly || null
    : null
);

/** The first-month price the parent named, if it is one the server offers
 * right now; null otherwise. A price kept from an earlier visit whose coupon
 * has since gone, or one from another arm, is treated as no choice: the card
 * shows the suggested price rather than a number checkout would refuse. */
export const validChosenCents = (plansData, variant, chosenCents) => {
  if (!Number.isInteger(chosenCents)) return null;
  const choose = pwywChoiceFor(plansData, variant, 'monthly');
  if (!choose) return null;
  if (Array.isArray(choose.stops)) return choose.stops.includes(chosenCents) ? chosenCents : null;
  const { min_cents: min, max_cents: max } = choose;
  return Number.isFinite(min) && Number.isFinite(max) && chosenCents >= min && chosenCents <= max ? chosenCents : null;
};

/** The offer this visitor is priced from: their arm's terms, with the price
 * they named (when the server offers it) laid over the first month in the
 * pwyw arm. `experiment` = { variant, chosenCents }. */
export const resolveFunnelOffer = (plansData, { variant, chosenCents } = {}) => {
  const base = funnelOfferFor(plansData, variant);
  return servedOfferVariant(plansData, variant) === 'pwyw'
    ? withChosenCents(base, validChosenCents(plansData, variant, chosenCents))
    : base;
};

/**
 * What annual billing saves against twelve monthly payments at the regular
 * prices (Pro $11.99/mo yearly vs $14.99 → "SAVE 20%"; with Family's 24%,
 * "SAVE UP TO 24%"), floored to a five and never overstated. The first
 * period is not counted: Pro's first year ($119.99) costs more than twelve
 * first months, so the saving is a renewal saving and is said as one. Null
 * until the prices are in, so a pill is never guessed.
 */
export const annualSavingsLabel = (prices, tiers = SELLABLE_TIERS) => {
  const labels = tiers.filter((t) => priceable(prices, t))
    .map((t) => discountLabel(prices[t].monthly * 12, prices[t].annual))
    .filter(Boolean);
  if (!labels.length) return null;
  const best = labels.map((l) => parseInt(l, 10)).reduce((a, b) => Math.max(a, b), 0);
  const label = labels.find((l) => parseInt(l, 10) === best);
  return labels.length > 1 && new Set(labels).size > 1 ? `SAVE UP TO ${label}` : `SAVE ${label}`;
};

/**
 * Which paywall this account gets, from /subscription-status:
 *   'winback' — opened from the win-back email (callers pass `offer`), non-members only, and only
 *               with the backend's terms for it in hand (`winbackOffer`: the coupon's percent and
 *               months and each plan's first-month price, read from Stripe) — otherwise the card
 *               would promise what checkout won't charge, so it is the normal paywall instead
 *   'trial'   — the account has a trial to offer (a Moments buyer), non-members only
 *   'plain'   — offers are off for this account (pre-cutover, or not eligible): list prices
 *   'normal'  — the normal paywall above
 */
export const paywallKind = ({ subscriptionStatus, offer = null, isMember = false } = {}) => {
  if (offer === 'winback' && !isMember && subscriptionStatus?.winbackOffer?.plans) return 'winback';
  if (!isMember && (subscriptionStatus?.trialConfig?.duration_days || 0) > 0) return 'trial';
  const intro = subscriptionStatus?.introOffer;
  if (isMember || intro?.enabled === false || intro?.eligible === false) return 'plain';
  return 'normal';
};

/**
 * The win-back card for one plan, from the backend's terms: the first-month
 * price it states, said as the coupon charges it. Null when the terms do not
 * cover the plan.
 */
export const winbackCard = (terms, tier) => {
  const plan = terms?.plans?.[tier];
  const months = Number(terms?.duration_months || 0);
  if (!plan || !Number.isInteger(plan.first_cents) || !Number.isInteger(plan.regular_cents) || months < 1) return null;
  const label = terms.percent_off ? `${Math.round(terms.percent_off)}%` : discountLabel(plan.regular_cents, plan.first_cents);
  return {
    kind: 'intro', tier, billing: 'monthly',
    discountPercent: Math.round((1 - plan.first_cents / plan.regular_cents) * 100),
    chip: label ? `${label} OFF` : '',
    regularCents: plan.regular_cents, firstCents: plan.first_cents,
    regularPrice: formatCents(plan.regular_cents), discountedPrice: formatCents(plan.first_cents),
    months,
    periodLabel: months > 1 ? `first ${months} months` : 'first month',
    renewalPrice: `${formatCents(plan.regular_cents)}/month`,
    monthlyEquivalent: formatCents(plan.regular_cents),
    choose: null, named: null,
  };
};

/**
 * One plan card on the normal paywall, priced from the plans response.
 *
 *   { kind: 'loading' }      — no plans response yet: show a spinner, never a number
 *   { kind: 'unavailable' }  — Stripe could not be read: PRICING_UNAVAILABLE + retry
 *   { kind: 'plain', … }     — offers off (`offersOn` false, or the server says so): the regular price
 *   { kind: 'intro', … }     — the first period's price, the chip ("45%+ OFF"), the renewal line, and
 *                              for Pro monthly in the pwyw arm `choose` (the chooser's terms) and
 *                              `named` (the stop the parent picked, when the server still offers it)
 */
export const cardOffer = (plansData, { tier, billing = 'monthly', variant = null, chosenCents = null, offersOn = true } = {}) => {
  if (!plansData) return { kind: 'loading', tier, billing };
  const prices = pricesFrom(plansData);
  const offer = resolveFunnelOffer(plansData, { variant, chosenCents });
  const terms = offer?.plans?.[tier];
  if (!priceable(prices, tier) || offer?.available === false || terms?.available === false) {
    return { kind: 'unavailable', tier, billing };
  }
  const annual = billing === 'annual';
  const first = terms?.first_period_cents?.[billing];
  const introOn = offersOn && offer?.enabled !== false && terms?.enabled !== false && Number.isInteger(first);
  // the list price is `plans[].price_cents`; an offer is priced against the regular price its own block states
  const regular = introOn && Number.isInteger(terms?.regular_cents?.[billing]) ? terms.regular_cents[billing] : prices[tier][billing];
  const base = {
    tier,
    billing,
    regularCents: regular,
    regularPrice: formatCents(regular),
    monthlyEquivalent: formatCents(annual ? Math.round(regular / 12) : regular),
    renewalPrice: `${formatCents(regular)}/${annual ? 'year' : 'month'}`,
    periodLabel: annual ? 'first year' : 'first month',
    choose: null,
    named: null,
  };
  if (!introOn) {
    return { ...base, kind: 'plain', firstCents: regular, discountedPrice: base.regularPrice, discountPercent: 0, chip: '' };
  }
  const choose = tier === 'pro' && !annual ? pwywChoiceFor(plansData, variant, 'monthly') : null;
  const named = choose ? validChosenCents(plansData, variant, chosenCents) : null;
  if (first >= regular) {
    // pay what you want at the regular price: a plain first month, nothing struck through
    return { ...base, kind: 'intro', firstCents: regular, discountedPrice: base.regularPrice, discountPercent: 0, chip: '', choose, named };
  }
  // said the way the backend says it ("45%+" for $7.99), never overstated
  const label = terms.discount_label?.[billing] || discountLabel(regular, first);
  return {
    ...base,
    kind: 'intro',
    firstCents: first,
    discountedPrice: formatCents(first),
    discountPercent: Math.round((1 - first / regular) * 100),
    chip: label ? `${label} OFF` : '',
    choose,
    named,
  };
};

/** The experiment properties every paywall reports on its checkout events. */
export const offerAnalytics = (plansData, { surface, variant, card = null }) => ({
  offer_surface: surface,
  offer_variant: variant || null,
  offer_served: servedOfferVariant(plansData, variant),
  chosen_cents: card?.named ?? null,
  first_period_cents: Number.isInteger(card?.firstCents) ? card.firstCents : null,
});
